import React, { useState } from 'react';
import {
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { useInput, InputProps } from 'react-admin';

interface TaskTagsInputProps extends Omit<InputProps, 'source'> {
  source: string;
  label?: string;
}

export const TaskTagsInput = ({ source, label = 'Tags', ...props }: TaskTagsInputProps) => {
  const { field, fieldState } = useInput({ source, ...props });
  const [inputValue, setInputValue] = useState('');
  
  // Ensure field.value is always an array
  const tags = Array.isArray(field.value) ? field.value : [];

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !tags.includes(trimmedValue)) {
      const newTags = [...tags, trimmedValue];
      field.onChange(newTags);
      setInputValue('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = tags.filter((tag: string) => tag !== tagToRemove);
    field.onChange(newTags);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
    if (event.key === ',') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Handle comma-separated input for quick tag addition
    if (value.includes(',')) {
      const newTag = value.replace(',', '').trim();
      if (newTag && !tags.includes(newTag)) {
        const newTags = [...tags, newTag];
        field.onChange(newTags);
      }
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <TagIcon sx={{ mr: 1 }} />
        {label}
      </Typography>
      
      {/* Tag Input Field */}
      <TextField
        fullWidth
        placeholder="Add a tag and press Enter or comma..."
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleAddTag}
                disabled={!inputValue.trim()}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        helperText="Press Enter, comma, or click + to add tags"
        error={!!fieldState.error}
        size="small"
        sx={{ mb: 2 }}
      />

      {/* Display Tags */}
      {tags.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Current Tags ({tags.length}):
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag: string, index: number) => (
              <Chip
                key={`${tag}-${index}`}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiChip-deleteIcon': {
                    fontSize: '16px',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
      
      {tags.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No tags added yet. Tags help categorize and search for tasks.
        </Typography>
      )}

      {/* Error Display */}
      {fieldState.error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {fieldState.error.message}
        </Typography>
      )}
    </Box>
  );
};